import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { Provider } from 'react-redux';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import '@fontsource/nunito/300.css';
import '@fontsource/nunito/400.css';
import '@fontsource/nunito/500.css';
import '@fontsource/nunito/600.css';
import '@fontsource/nunito/700.css';

// Redux
import { store } from './redux/store';
import { useAppSelector } from './redux/hooks';

// Context Providers
import { NotificationProvider } from './contexts/NotificationContext';

// Theme
import getTheme from './config/theme';

// Pages
import CategoryPage from './pages/CategoryPage';
import Dashboard from './pages/Dashboard';
import Home from './pages/Home';
import Login from './pages/Login';
import ProtocolDetail from './pages/ProtocolDetail';
import Protocols from './pages/protocols/Protocols';
import Register from './pages/Register';
import Search from './pages/Search';
import SurgicalProcedures from './pages/surgical-procedures/SurgicalProcedures';

// Components
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/common/ProtectedRoute';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  },
});

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
};

// Inner App component that has access to Redux
const AppContent: React.FC = () => {
  const theme = useAppSelector(state => state.ui.theme);
  const muiTheme = getTheme(theme);

  return (
    <MUIThemeProvider theme={muiTheme}>
      <CssBaseline />
      <ErrorBoundary>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            <NotificationProvider>
              <Router>
                <Routes>
                  {/* Public Routes */}
                  <Route path='/' element={<Home />} />
                  <Route path='/protocols' element={<Protocols />} />
                  <Route path='/protocols/:id' element={<ProtocolDetail />} />
                  <Route path='/surgical-procedures' element={<SurgicalProcedures />} />
                  <Route path='/search' element={<Search />} />
                  <Route path='/categories/:category' element={<CategoryPage />} />
                  <Route path='/login' element={<Login />} />
                  <Route path='/register' element={<Register />} />
                  <Route
                    path='/dashboard'
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path='/profile'
                    element={
                      <ProtectedRoute>
                        <div>Profile - Coming Soon</div>
                      </ProtectedRoute>
                    }
                  />

                  {/* Catch all route */}
                  <Route path='*' element={<Navigate to='/protocols' replace />} />
                </Routes>

                {/* Toast Notifications */}
                <Toaster
                  position='top-right'
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: '#363636',
                      color: '#fff',
                    },
                    success: {
                      duration: 3000,
                      iconTheme: {
                        primary: '#4ade80',
                        secondary: '#fff',
                      },
                    },
                    error: {
                      duration: 5000,
                      iconTheme: {
                        primary: '#ef4444',
                        secondary: '#fff',
                      },
                    },
                  }}
                />
              </Router>
            </NotificationProvider>
          </QueryClientProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </MUIThemeProvider>
  );
};

export default App;
